import { useMemo, useState } from 'react';

import { Check, ChevronDown } from 'lucide-react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { cn } from '@/lib/utils';

import { EntityAvatar } from '../avatar/entity-avatar';

export interface SingleSelectOption {
  value: string;
  label: string;
  description?: string;
  avatar?: string | null;
  color?: string;
  disabled?: boolean;
  searchValue?: string; // Custom search string if different from label
  customContent?: React.ReactNode; // For completely custom option rendering
}

interface SingleSelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  options: SingleSelectOption[];
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  noOptionsMessage?: string;
  searchPlaceholder?: string;
  loadingMoreMessage?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;

  // Customization options
  searchable?: boolean;
  hideSearchWhenEmpty?: boolean; // Hide search input when no options available
  enableClientSideFiltering?: boolean; // Enable local filtering for search (default: false for server-side)
  showAvatars?: boolean;
  showDescriptionInOptions?: boolean;
  showDescriptionInSelector?: boolean;
  avatarShape?: 'circle' | 'square' | 'rounded';
  maxHeight?: string;

  // Custom rendering
  renderOption?: (option: SingleSelectOption, isSelected: boolean) => React.ReactNode;
  renderTrigger?: (
    selectedOption: SingleSelectOption | null,
    placeholder: string
  ) => React.ReactNode;

  // Override for selected option (useful when selected option may not be in current options list)
  selectedOption?: SingleSelectOption | null;

  // Callbacks
  onOpenChange?: (open: boolean) => void;
  onSearchChange?: (value: string) => void;

  // Pagination support
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

/**
 * A highly customizable single-select dropdown component with React Hook Form integration.
 * Automatically switches between simple Select and advanced Command-based implementations based on features needed.
 *
 * **Key Features:**
 * - **Dual Implementation**: Uses shadcn Select for simple cases, Command for advanced features
 * - **React Hook Form Integration**: Full validation and error handling support
 * - **Rich Content Support**: Avatars, descriptions, custom rendering, and search functionality
 * - **Granular Description Control**: Separate control for descriptions in options vs selector
 * - **Smart Performance**: Only loads complex UI when search or advanced features are needed
 * - **Accessibility**: Full keyboard navigation, screen reader support, and ARIA compliance
 * - **Customizable**: Extensive theming, rendering, and behavior customization options
 * - **Loading States**: Built-in loading handling with disabled states
 * - **Flexible Filtering**: Both client-side and server-side search support
 *
 * **Implementation Strategy:**
 * - **Simple Mode** (`searchable={false}`): Uses optimized shadcn Select component
 * - **Advanced Mode** (`searchable={true}`): Uses Command with Popover for rich features
 *
 * **Content Types Supported:**
 * - Plain text options with labels
 * - Options with descriptions and metadata
 * - Avatar-enabled options (user/project selection)
 * - Completely custom option rendering
 * - Mixed content types within single dropdown
 *
 * @example
 * ```tsx
 * // Basic usage with React Hook Form
 * function ProjectForm() {
 *   const form = useForm<{ managerId: string }>();
 *
 *   return (
 *     <SingleSelect
 *       name="managerId"
 *       control={form.control}
 *       label="Project Manager"
 *       placeholder="Select a manager"
 *       options={[
 *         { value: "1", label: "John Doe" },
 *         { value: "2", label: "Jane Smith" },
 *         { value: "3", label: "Bob Johnson" },
 *       ]}
 *     />
 *   );
 * }
 *
 * // Rich user selection with avatars and descriptions in both options and selector
 * <SingleSelect
 *   name="assignee"
 *   control={form.control}
 *   label="Assign to Team Member"
 *   placeholder="Choose team member"
 *   showAvatars={true}
 *   showDescriptionInOptions={true}
 *   showDescriptionInSelector={true}
 *   options={[
 *     {
 *       value: "user1",
 *       label: "Sarah Wilson",
 *       description: "Senior Developer",
 *       avatar: "/avatars/sarah.jpg",
 *       color: "#ff6b6b"
 *     },
 *     {
 *       value: "user2",
 *       label: "Mike Chen",
 *       description: "Product Manager",
 *       avatar: "/avatars/mike.jpg",
 *       color: "#4ecdc4"
 *     }
 *   ]}
 * />
 *
 * // Show descriptions only in options, not in selector (cleaner trigger)
 * <SingleSelect
 *   name="project"
 *   control={form.control}
 *   label="Select Project"
 *   placeholder="Choose project"
 *   showAvatars={true}
 *   showDescriptionInOptions={true}
 *   showDescriptionInSelector={false}
 *   options={projectOptions}
 * />
 *
 * // Show descriptions only in selector, not in options (cleaner dropdown)
 * <SingleSelect
 *   name="status"
 *   control={form.control}
 *   label="Status"
 *   placeholder="Select status"
 *   showDescriptionInOptions={false}
 *   showDescriptionInSelector={true}
 *   options={statusOptions}
 * />
 *
 * // Simple select without search (optimal performance)
 * <SingleSelect
 *   name="priority"
 *   control={form.control}
 *   label="Priority Level"
 *   searchable={false}
 *   showDescriptionInOptions={false}
 *   showDescriptionInSelector={false}
 *   options={[
 *     { value: "low", label: "Low Priority" },
 *     { value: "medium", label: "Medium Priority" },
 *     { value: "high", label: "High Priority" },
 *     { value: "urgent", label: "Urgent" }
 *   ]}
 * />
 *
 * // Client-side filtering for local data
 * <SingleSelect
 *   name="managerId"
 *   control={form.control}
 *   label="Project Manager"
 *   searchable={true}
 *   enableClientSideFiltering={true}
 *   showAvatars={true}
 *   options={managers}
 * />
 *
 * // Advanced customization with custom rendering
 * <SingleSelect
 *   name="project"
 *   control={form.control}
 *   label="Select Project"
 *   showAvatars={true}
 *   avatarShape="rounded"
 *   filterFunction={(option, search) => {
 *     // Custom search: include project codes and descriptions
 *     return option.label.toLowerCase().includes(search.toLowerCase()) ||
 *            option.searchValue?.toLowerCase().includes(search.toLowerCase());
 *   }}
 *   renderOption={(option, isSelected) => (
 *     <div className="flex items-center justify-between w-full">
 *       <div className="flex items-center gap-3">
 *         <EntityAvatar color={option.color} name={option.label} size="sm" />
 *         <div>
 *           <div className="font-medium">{option.label}</div>
 *           <div className="text-xs text-muted-foreground">{option.description}</div>
 *         </div>
 *       </div>
 *       <div className="text-xs text-muted-foreground">
 *         {option.searchValue}
 *       </div>
 *       {isSelected && <Check className="h-4 w-4" />}
 *     </div>
 *   )}
 *   options={[
 *     {
 *       value: "proj1",
 *       label: "E-commerce Platform",
 *       description: "Customer portal redesign",
 *       searchValue: "ECOM-2024",
 *       color: "#8b5cf6"
 *     },
 *     {
 *       value: "proj2",
 *       label: "Mobile App",
 *       description: "iOS and Android development",
 *       searchValue: "MOB-2024",
 *       color: "#06b6d4"
 *     }
 *   ]}
 * />
 *
 * // Loading and disabled states
 * function DynamicSelect() {
 *   const { data: options, isLoading } = useQuery(['teams'], fetchTeams);
 *
 *   return (
 *     <SingleSelect
 *       name="teamId"
 *       control={form.control}
 *       label="Team"
 *       loading={isLoading}
 *       disabled={!userCanAssignTeams}
 *       options={options || []}
 *       noOptionsMessage="No teams available"
 *       emptyMessage="No teams match your search"
 *     />
 *   );
 * }
 *
 * // Complex validation example
 * const schema = z.object({
 *   department: z.string().min(1, 'Please select a department')
 * });
 *
 * function ValidatedForm() {
 *   const form = useForm<z.infer<typeof schema>>({
 *     resolver: zodResolver(schema)
 *   });
 *
 *   return (
 *     <SingleSelect
 *       name="department"
 *       control={form.control}
 *       label="Department"
 *       options={departments}
 *       // Form validation errors will appear automatically
 *     />
 *   );
 * }
 *
 * // Custom content with badges and status indicators
 * <SingleSelect
 *   name="environment"
 *   control={form.control}
 *   label="Deployment Environment"
 *   options={[
 *     {
 *       value: "prod",
 *       label: "Production",
 *       customContent: (
 *         <div className="flex items-center justify-between w-full">
 *           <span>Production</span>
 *           <Badge variant="destructive">Critical</Badge>
 *         </div>
 *       )
 *     },
 *     {
 *       value: "staging",
 *       label: "Staging",
 *       customContent: (
 *         <div className="flex items-center justify-between w-full">
 *           <span>Staging</span>
 *           <Badge variant="secondary">Safe</Badge>
 *         </div>
 *       )
 *     }
 *   ]}
 * />
 * ```
 */
export function SingleSelect<T extends FieldValues>({
  name,
  control,
  options,
  label,
  placeholder = 'Select an option',
  emptyMessage = 'No results found',
  noOptionsMessage,
  searchPlaceholder,
  disabled = false,
  loading = false,
  className,
  searchable = true,
  hideSearchWhenEmpty = false,
  enableClientSideFiltering = true,
  showAvatars = false,
  showDescriptionInOptions = false,
  showDescriptionInSelector = false,
  avatarShape = 'circle',
  maxHeight = '300px',
  renderOption,
  renderTrigger,
  selectedOption: overrideSelectedOption,
  onOpenChange,
  onSearchChange,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: SingleSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { t } = useTranslation(['common']);

  // Set up intersection observer for infinite scroll
  const { setContainerElement, setSentinelElement } = useInfiniteScroll({
    hasNextPage,
    isFetching: isFetchingNextPage,
    onLoadMore: fetchNextPage || (() => {}),
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchValue('');
      onSearchChange?.('');
    }
    onOpenChange?.(open);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  // Apply filtering based on search mode
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchValue.trim() || !enableClientSideFiltering) {
      return options;
    }

    const searchTerm = searchValue.toLowerCase();
    return options.filter(option => {
      return (
        option.label.toLowerCase().includes(searchTerm) ||
        (option.description && option.description.toLowerCase().includes(searchTerm)) ||
        (option.searchValue && option.searchValue.toLowerCase().includes(searchTerm))
      );
    });
  }, [options, searchValue, searchable, enableClientSideFiltering]);

  // Enhanced default option renderer with better accessibility and visual hierarchy
  const defaultRenderOption = (option: SingleSelectOption, isSelected: boolean) => (
    <div
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm py-1 pr-8 pl-1 text-sm outline-none',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground',
        isSelected && 'bg-accent text-accent-foreground'
      )}
    >
      {/* Check icon - positioned at the right edge with proper spacing */}
      <span className="absolute right-2 flex h-4 w-4 items-center justify-center">
        <Check
          className={cn(
            'h-4 w-4 transition-opacity',
            isSelected ? 'opacity-100 text-primary' : 'opacity-0'
          )}
        />
      </span>

      {/* Avatar with consistent sizing */}
      {showAvatars && (
        <EntityAvatar
          image={option.avatar}
          color={option.color}
          name={option.label}
          size="sm"
          shape={avatarShape}
          className="mr-3 flex-shrink-0"
        />
      )}

      {/* Content with improved typography hierarchy */}
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'truncate font-normal leading-tight',
            isSelected ? 'text-accent-foreground' : 'text-foreground'
          )}
        >
          {option.label}
        </div>
        {showDescriptionInOptions && option.description && (
          <div
            className={cn(
              'text-xs text-muted-foreground truncate mt-0.5 leading-tight',
              isSelected && 'text-accent-foreground/70'
            )}
          >
            {option.description}
          </div>
        )}
      </div>
    </div>
  );

  // Enhanced default trigger renderer with better loading states and typography
  const defaultRenderTrigger = (
    selectedOption: SingleSelectOption | null,
    placeholderText: string
  ) => (
    <div className="flex min-h-[1.5rem] w-full items-center">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {selectedOption ? (
          <>
            {showAvatars && (
              <EntityAvatar
                image={selectedOption.avatar}
                color={selectedOption.color}
                name={selectedOption.label}
                size="sm"
                shape={avatarShape}
                className="!h-5 !w-5 flex-shrink-0"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-normal leading-tight">
                {selectedOption.label}
              </div>
              {showDescriptionInSelector && selectedOption.description && (
                <div className="truncate text-xs leading-tight text-muted-foreground">
                  {selectedOption.description}
                </div>
              )}
            </div>
          </>
        ) : (
          <span
            className={cn(
              'font-normal truncate text-sm',
              loading ? 'text-muted-foreground animate-pulse' : 'text-muted-foreground'
            )}
          >
            {loading ? t('loadingOptions') : placeholderText}
          </span>
        )}
      </div>
      <ChevronDown
        className={cn(
          'ml-2 h-4 w-4 flex-shrink-0 transition-transform',
          'opacity-50',
          isOpen && 'rotate-180'
        )}
      />
    </div>
  );

  // Use shadcn Select for all cases except when search is needed
  const useSimpleSelect = !searchable;

  if (useSimpleSelect) {
    return (
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className="w-full">
            {label && <FormLabel>{label}</FormLabel>}
            <Select
              value={field.value || ''}
              onValueChange={field.onChange}
              onOpenChange={disabled || loading ? undefined : handleOpenChange}
              disabled={disabled || loading}
            >
              <FormControl>
                <SelectTrigger className={className}>
                  {renderTrigger && field.value ? (
                    renderTrigger(
                      options.find(opt => opt.value === field.value) || null,
                      placeholder
                    )
                  ) : (
                    <SelectValue placeholder={loading ? 'Loading...' : placeholder} />
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {noOptionsMessage || 'No options available'}
                  </div>
                ) : (
                  options.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      checkPosition="right"
                      itemTextAsChild={!!option.customContent}
                    >
                      {option.customContent ? (
                        option.customContent
                      ) : (
                        <div className="flex w-full items-center">
                          {/* Avatar */}
                          {showAvatars && (
                            <EntityAvatar
                              image={option.avatar}
                              color={option.color}
                              name={option.label}
                              size="sm"
                              shape={avatarShape}
                              className="mr-3 flex-shrink-0"
                            />
                          )}

                          {/* Content */}
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{option.label}</div>
                            {showDescriptionInOptions && option.description && (
                              <div className="truncate text-sm text-muted-foreground">
                                {option.description}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  // Use complex Command-based interface for advanced features
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedOption =
          overrideSelectedOption || options.find(option => option.value === field.value) || null;

        return (
          <FormItem className="flex flex-col">
            {label && <FormLabel>{label}</FormLabel>}
            <Popover
              open={disabled ? false : isOpen}
              onOpenChange={disabled ? undefined : handleOpenChange}
            >
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isOpen}
                    className={cn(
                      'w-full justify-start text-left h-auto min-h-[2.5rem] py-2 px-3',
                      'disabled:border-input disabled:opacity-100 disabled:bg-muted disabled:pointer-events-none',
                      !selectedOption && 'text-muted-foreground',
                      className
                    )}
                    disabled={disabled || loading}
                  >
                    {renderTrigger
                      ? renderTrigger(selectedOption, placeholder)
                      : defaultRenderTrigger(selectedOption, placeholder)}
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command shouldFilter={false}>
                  {searchable && !(hideSearchWhenEmpty && options.length === 0) && (
                    <CommandInput
                      placeholder={searchPlaceholder || `Search ${placeholder.toLowerCase()}...`}
                      value={searchValue}
                      onValueChange={handleSearchChange}
                      className="border-0 focus:ring-0"
                    />
                  )}
                  <CommandList
                    ref={setContainerElement}
                    className="max-h-[300px] overflow-auto"
                    style={{ maxHeight }}
                  >
                    {filteredOptions.length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        {options.length === 0
                          ? noOptionsMessage || 'No options available'
                          : emptyMessage || 'No results found'}
                      </div>
                    ) : (
                      <CommandGroup>
                        {filteredOptions.map((option, index) => {
                          const isSelected = field.value === option.value;
                          const handleSelect = () => {
                            if (!option.disabled) {
                              field.onChange(option.value);
                              handleOpenChange(false);
                            }
                          };

                          return (
                            <CommandItem
                              key={`${option.value}-${index}`}
                              value={option.value}
                              onSelect={handleSelect}
                              className={cn(
                                'cursor-pointer py-1',
                                option.disabled && 'opacity-50 cursor-not-allowed'
                              )}
                              disabled={option.disabled}
                            >
                              {option.customContent ||
                                (renderOption
                                  ? renderOption(option, isSelected)
                                  : defaultRenderOption(option, isSelected))}
                            </CommandItem>
                          );
                        })}
                        {isFetchingNextPage && (
                          <div className="py-2 text-center text-xs text-muted-foreground">
                            {t('loadingMore')}
                          </div>
                        )}
                        {/* Sentinel element for infinite scroll detection */}
                        {hasNextPage && <div ref={setSentinelElement} className="h-1" />}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
